import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Crown, LogOut, BookOpen, Clock, Flame, Sparkles, Check, Globe, ArrowRight, CreditCard, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useOnboarding } from "@/context/OnboardingContext";
import { useTranslation } from "@/context/LanguageContext";
import { LANGUAGES } from "@/i18n";
import { analyticsApi, billingApi } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function Profile() {
    const { user, logout } = useAuth();
    const { startTour } = useOnboarding();
    const { t, lang, changeLanguage } = useTranslation();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        (async () => { try { const d = await analyticsApi.overview(); setStats(d.stats); } catch (e) { /* ignore */ } })();
    }, []);

    const tiers = t("profile.tiers");

    return (
        <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:py-10">
            <h1 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("profile.title")}</h1>
            <p className="mt-1.5 text-t3">{t("profile.subtitle")}</p>

            <div className="mt-6 rounded-2xl border border-border bg-surface-1 p-6" data-testid="profile-card">
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-4">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-surface-3 font-sans-display text-2xl font-semibold uppercase text-[hsl(var(--primary))]">
                            {(user?.name || "L").slice(0, 1)}
                        </div>
                        <div>
                            <div className="font-sans-display text-lg font-semibold text-t1">{user?.name}</div>
                            <div className="mt-0.5 flex items-center gap-1.5 text-sm text-t3"><Mail className="h-3.5 w-3.5" /> {user?.email}</div>
                            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))]/15 px-2.5 py-1 text-xs font-medium text-[hsl(var(--primary))]">
                                <Crown className="h-3.5 w-3.5" /> {(user?.tier || "free").toUpperCase()} {t("profile.planSuffix")}
                            </div>
                        </div>
                    </div>
                    <Button data-testid="profile-logout-button" variant="secondary" className="gap-2 bg-surface-3/60" onClick={() => { logout(); navigate("/login"); }}>
                        <LogOut className="h-4 w-4" /> {t("profile.signOut")}
                    </Button>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3">
                    <div className="rounded-xl bg-surface-2 p-4 text-center">
                        <BookOpen className="mx-auto h-4 w-4 text-[hsl(var(--primary))]" />
                        <div className="mt-2 font-sans-display text-xl font-semibold text-t1">{stats?.library_count ?? "–"}</div>
                        <div className="mono-label text-t4">{t("profile.library")}</div>
                    </div>
                    <div className="rounded-xl bg-surface-2 p-4 text-center">
                        <Clock className="mx-auto h-4 w-4 text-[hsl(var(--info))]" />
                        <div className="mt-2 font-sans-display text-xl font-semibold text-t1">{stats?.hours_read ?? "–"}</div>
                        <div className="mono-label text-t4">{t("profile.hours")}</div>
                    </div>
                    <div className="rounded-xl bg-surface-2 p-4 text-center">
                        <Flame className="mx-auto h-4 w-4 text-[hsl(var(--warning))]" />
                        <div className="mt-2 font-sans-display text-xl font-semibold text-t1">{stats?.reading_streak ?? "–"}</div>
                        <div className="mono-label text-t4">{t("profile.streak")}</div>
                    </div>
                </div>
            </div>

            {/* Language */}
            <div className="mt-6 rounded-2xl border border-border bg-surface-1 p-6" data-testid="profile-language-card">
                <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-[hsl(var(--primary))]" />
                    <h2 className="font-sans-display text-sm font-semibold text-t2">{t("profile.language")}</h2>
                </div>
                <p className="mt-1 text-xs text-t4">{t("profile.languageHelp")}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                    {LANGUAGES.map((l) => {
                        const active = l.code === lang;
                        return (
                            <button
                                key={l.code}
                                data-testid={`profile-language-${l.code}`}
                                onClick={() => changeLanguage(l.code)}
                                aria-pressed={active}
                                className={cn(
                                    "inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--focus))]",
                                    active ? "border-[hsl(var(--primary))]/55 bg-[hsl(var(--primary))]/10 text-t1" : "border-border bg-surface-2 text-t2 hover:bg-surface-3"
                                )}
                            >
                                <span className="text-base leading-none">{l.flag}</span>
                                {l.label}
                                {active && <Check className="h-4 w-4 text-[hsl(var(--primary))]" />}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Product tour */}
            <div className="mt-6 rounded-2xl border border-border bg-surface-1 p-6" data-testid="profile-tour-card">
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"><Compass className="h-5 w-5" /></div>
                        <div>
                            <h2 className="font-sans-display text-sm font-semibold text-t2">{t("onboarding.restart")}</h2>
                            <p className="mt-1 text-xs text-t4">{t("onboarding.restartHelp")}</p>
                        </div>
                    </div>
                    <Button data-testid="profile-restart-tour-button" variant="secondary" className="gap-2 bg-surface-3/60" onClick={startTour}>
                        <Compass className="h-4 w-4" /> {t("onboarding.restart")}
                    </Button>
                </div>
            </div>

            {/* Plans */}
            <div className="mt-8">
                <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" />
                        <h2 className="font-sans-display text-sm font-semibold text-t2">{t("profile.plans")}</h2>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button data-testid="profile-view-plans-button" size="sm" className="gap-1.5" onClick={() => navigate("/pricing")}>
                            {t("profile.viewPlans")} <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            data-testid="profile-manage-billing-button"
                            size="sm"
                            variant="secondary"
                            className="gap-1.5 bg-surface-3/60"
                            onClick={async () => {
                                try {
                                    const res = await billingApi.portal(window.location.origin);
                                    if (res.configured && res.url) window.location.href = res.url;
                                    else toast.info(t("billing.notConfigured"));
                                } catch (e) { toast.info(t("billing.notConfigured")); }
                            }}
                        >
                            <CreditCard className="h-3.5 w-3.5" /> {t("profile.manageBilling")}
                        </Button>
                    </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {tiers.map((tier) => {
                        const current = (user?.tier || "free").toLowerCase() === tier.name.toLowerCase();
                        return (
                            <div key={tier.name} data-testid={`profile-plan-${tier.name.toLowerCase()}`} className={`rounded-2xl border p-5 ${current ? "border-[hsl(var(--primary))]/50 bg-surface-2" : "border-border bg-surface-1"}`}>
                                <div className="flex items-center justify-between">
                                    <span className="font-sans-display text-base font-semibold text-t1">{tier.name}</span>
                                    {current && <span className="mono-label rounded-full bg-[hsl(var(--primary))] px-2 py-0.5 text-[9px] text-[hsl(var(--primary-foreground))]">{t("profile.current")}</span>}
                                </div>
                                <p className="mt-1 text-xs text-t4">{tier.desc}</p>
                                <ul className="mt-3 space-y-1.5">
                                    {tier.features.map((f) => (
                                        <li key={f} className="flex items-start gap-1.5 text-xs text-t3"><Check className="mt-0.5 h-3 w-3 shrink-0 text-[hsl(var(--success))]" />{f}</li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
